import React from 'react';
import { get, set, size, sortBy, snakeCase } from 'global/lib/lodash';
import { Typography, Chip } from '@mui/material';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useLoaded } from 'global/hooks/useLoaded';
import { getTaskStatus, uppercaseFirst } from 'global/utils';
import moment from 'moment';
import makeStyles from '@mui/styles/makeStyles';
import { useTaskTypes } from 'feature/task/task-type-utils/useTaskTypes';
import {
  getTaskTypeById,
  getTaskTypeIdFromTask,
  getStandardFieldRequirements,
  getCustomFieldMediaIds,
} from 'feature/task/task-type-utils';
import { getTradeName } from 'global/components/trade-select';
import { TaskScoreRef } from 'feature/task/CreateModal';

const LABEL_FONT_SIZE = 13;
const LABEL_MIN_WIDTH = 120;

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: 30,
    fontWeight: 'bold',
    textAlign: 'center',
    paddingTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  taskCard: {
    pageBreakInside: 'avoid',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: '1px solid #ddd',
    borderRadius: 4,
  },
  taskHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  taskName: {
    fontSize: 16,
    fontWeight: 'bold',
    flex: 1,
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  fieldRow: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
    fontSize: LABEL_FONT_SIZE,
  },
  fieldLabel: {
    fontSize: LABEL_FONT_SIZE,
    fontWeight: 'bold',
    minWidth: LABEL_MIN_WIDTH,
    color: '#666',
  },
  fieldValue: {
    flex: 1,
    fontSize: LABEL_FONT_SIZE,
    whiteSpace: 'pre-wrap',
  },
  richTextValue: {
    flex: 1,
    fontSize: LABEL_FONT_SIZE,
    '& > *:first-child': { marginTop: 0 },
    '& > *:last-child': { marginBottom: 0 },
    '& p': { margin: 0 },
    '& p + p': { marginTop: theme.spacing(0.5) },
    '& ul, & ol': { marginTop: 0, marginBottom: 0, paddingLeft: theme.spacing(3) },
  },
  mediaSection: {
    display: 'flex',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mediaGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    flex: 1,
  },
  mediaItem: {
    width: 240,
    textAlign: 'center',
  },
  mediaImage: {
    width: 240,
    height: 180,
    objectFit: 'cover',
    borderRadius: 4,
    border: '1px solid #ddd',
  },
  mediaCaption: {
    fontSize: LABEL_FONT_SIZE,
    color: '#666',
    marginTop: 2,
  },
  repeaterSection: {
    marginBottom: theme.spacing(1),
  },
  repeaterItem: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    padding: `${theme.spacing(1)} 0`,
    borderBottom: '1px dashed #eee',
    '&:last-child': { borderBottom: 'none' },
  },
  repeaterItemTitle: {
    fontWeight: 'bold',
    fontSize: LABEL_FONT_SIZE,
    marginBottom: theme.spacing(0.5),
  },
  referenceSection: {
    display: 'flex',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    fontSize: LABEL_FONT_SIZE,
  },
  commentSection: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: '1px dashed #ccc',
  },
  comment: {
    fontSize: 12,
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    borderLeft: '2px solid #e0e0e0',
  },
  commentAuthor: {
    fontWeight: 'bold',
    fontSize: 11,
    color: '#666',
  },
  noTasks: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: '#999',
    fontStyle: 'italic',
  },
}));

const isHtml = (str) => typeof str === 'string' && /<[a-z][\s\S]*>/i.test(str);
const isIsoDate = (str) => typeof str === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(str);

const formatFieldValue = (val, classes) => {
  if (val === undefined || val === null || val === '') return null;
  const str = String(val);
  if (isIsoDate(str)) return <span className={classes.fieldValue}>{moment(str).format('L')}</span>;
  if (isHtml(str)) return <span className={classes.richTextValue} dangerouslySetInnerHTML={{ __html: str }} />;
  return <span className={classes.fieldValue}>{str}</span>;
};

const getNestedFieldLabels = (cf) => {
  const nested = get(cf, 'config.nested_fields', []);
  const map = {};
  nested.forEach((nf) => {
    const label = nf.label || nf.type;
    map[label] = label;
    const camel = label.replace(/[^a-zA-Z0-9]+(.)/g, (_, c) => c.toUpperCase()).replace(/^(.)/, (c) => c.toLowerCase());
    map[camel] = label;
  });
  return map;
};

const FieldRow = ({ classes, label, children }) => (
  <div className={classes.fieldRow}>
    <span className={classes.fieldLabel}>{label}:</span>
    {children}
  </div>
);

const MediaBlock = ({ classes, label, images, files = [] }) => {
  if (!size(images) && !size(files)) return null;
  return (
    <div className={classes.mediaSection}>
      <span className={classes.fieldLabel}>{label}:</span>
      <div className={classes.mediaGrid}>
        {images.map((img, idx) => (
          <div key={get(img, 'id', idx)} className={classes.mediaItem}>
            <img
              src={get(img, 'fullUrl', get(img, 'largeUrl', get(img, 'thumbUrl', '')))}
              alt={get(img, 'caption', '')}
              className={classes.mediaImage}
            />
            {get(img, 'caption') && <div className={classes.mediaCaption}>{img.caption}</div>}
          </div>
        ))}
        {files.map((file) => {
          const url = get(file, 'fullUrl', get(file, 'full7DayUrl', get(file, 'originalUrl', '')));
          const fileName = get(file, 'name', 'File');
          const inner = (
            <div className={classes.mediaImage} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', padding: 8, wordBreak: 'break-word', textAlign: 'center' }}>
              {fileName}
            </div>
          );
          return (
            <div key={file.id} className={classes.mediaItem}>
              {url ? (
                <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                  {inner}
                </a>
              ) : inner}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Custom-field media values are stored as cleaned {type:'media', id} refs.
// Resolve each ref against task.images / task.files (the JSON:API relationships
// hold the full objects with URLs and collection_name) and split into image vs
// file buckets so MediaBlock renders documents as files rather than broken <img>s.
const resolveAndSplitMedia = (refs, task) => {
  const allImages = get(task, 'images', []) || [];
  const allFiles = get(task, 'files', []) || [];
  const imageMap = new Map(allImages.map((m) => [String(m.id), m]));
  const fileMap = new Map(allFiles.map((m) => [String(m.id), m]));
  const images = [];
  const files = [];
  (refs || []).forEach((ref) => {
    if (!ref) return;
    const id = String(get(ref, 'id', ''));
    const resolved = (id && (imageMap.get(id) || fileMap.get(id))) || ref;
    const col = resolved.collectionName || resolved.collection_name;
    if (col === 'task-files' || (id && fileMap.has(id))) {
      files.push(resolved);
    } else {
      images.push(resolved);
    }
  });
  return { images, files };
};

const CustomFieldBlock = ({ classes, cf, customFieldValues, task }) => {
  const cfVal = get(customFieldValues, cf.id, get(customFieldValues, cf.handle));
  if (cfVal === undefined || cfVal === null || cfVal === '') return null;
  const label = cf.label || cf.name;

  // Media arrays
  if (Array.isArray(cfVal) && cfVal.length > 0 && cfVal[0] && (cfVal[0].type === 'media' || cfVal[0].fullUrl)) {
    const { images, files } = resolveAndSplitMedia(cfVal.filter(Boolean), task);
    if (!size(images) && !size(files)) return null;
    return <MediaBlock classes={classes} label={label} images={images} files={files} />;
  }

  // Repeater arrays
  if (Array.isArray(cfVal) && cfVal.length > 0 && cfVal[0] && typeof cfVal[0] === 'object' && !cfVal[0].id) {
    const labelMap = getNestedFieldLabels(cf);
    const itemName = get(cf, 'config.item_name', label);
    return (
      <div className={classes.repeaterSection}>
        <FieldRow classes={classes} label={label}>
          <span className={classes.fieldValue} />
        </FieldRow>
        {cfVal.map((item, idx) => (
          <div key={idx} className={classes.repeaterItem}>
            <div className={classes.repeaterItemTitle}>{itemName} #{idx + 1}</div>
            {Object.entries(item).filter(([k]) => k !== '_key').map(([key, val]) => {
              const nestedLabel = labelMap[key] || key;
              if (Array.isArray(val) && val.length > 0 && val[0] && (val[0].type === 'media' || val[0].fullUrl)) {
                const { images, files } = resolveAndSplitMedia(val.filter(Boolean), task);
                if (!size(images) && !size(files)) return null;
                return <MediaBlock key={key} classes={classes} label={nestedLabel} images={images} files={files} />;
              }
              if (val === undefined || val === null || val === '') return null;
              const formatted = formatFieldValue(val, classes);
              if (!formatted) return null;
              return (
                <FieldRow key={key} classes={classes} label={nestedLabel}>
                  {formatted}
                </FieldRow>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  // Simple arrays (multi-select, etc)
  if (Array.isArray(cfVal)) {
    const display = cfVal.filter((v) => v !== null && v !== undefined && v !== '').join(', ');
    if (!display) return null;
    return (
      <FieldRow classes={classes} label={label}>
        <span className={classes.fieldValue}>{display}</span>
      </FieldRow>
    );
  }

  const formatted = formatFieldValue(cfVal, classes);
  if (!formatted) return null;
  return (
    <FieldRow classes={classes} label={label}>
      {formatted}
    </FieldRow>
  );
};

const TaskPrintCard = ({ task, taskTypesData }) => {
  const classes = useStyles();
  const status = getTaskStatus(task);
  const assignees = get(task, 'assignees', []);
  const comments = get(task, 'comments', []);
  const dueDate = get(task, 'dueDate');
  const tradeTypes = get(task, 'tradeTypes', []);
  const author = get(task, 'authors.0.name', 'Unknown');
  const taskType = getTaskTypeById(taskTypesData, getTaskTypeIdFromTask(task));
  const customFieldValues = get(
    task,
    'customFieldValues',
    get(task, 'custom_field_values', get(task, 'meta.custom_field_values', get(task, 'meta.customFieldValues', {})))
  );

  // Filter out custom-field media from task-level media so images don't appear twice
  const cfMediaIds = new Set((getCustomFieldMediaIds(customFieldValues) || []).map(String));
  const images = sortBy(get(task, 'images', []), 'orderColumn').filter((img) => !cfMediaIds.has(String(img.id)));
  const files = get(task, 'files', []).filter((file) => !cfMediaIds.has(String(file.id)));

  // Reference info
  const scorecardScore = get(task, 'scorecardScore');
  const subScore = get(task, 'subScore');
  const reportSection = get(task, 'reportSection');
  const refCheckpoint = get(scorecardScore, 'checkpoint.name') || get(subScore, 'scorecardScore.checkpoint.name');
  const refChecklist = get(scorecardScore, 'checklist.name') || get(subScore, 'scorecardScore.checklist.name') || get(subScore, 'checklist.name');
  const hasReference = refCheckpoint || refChecklist || reportSection;

  const scoreRefTag = (() => {
    if (scorecardScore) {
      const cp = get(scorecardScore, 'checkpoint');
      return { ...cp, ...scorecardScore, pssvCheckpoint: cp, pssvChecklist: get(scorecardScore, 'checklist'), scorecardScoreId: get(task, 'scorecardScoreId', get(scorecardScore, 'id')) };
    }
    if (subScore) {
      return { ...get(subScore, 'scorecardScore', {}), ...subScore, pssvCheckpoint: subScore, pssvChecklist: get(subScore, 'checklist'), scorecardScoreId: get(subScore, 'scorecardScoreId', get(subScore, 'scorecardScore.id')) };
    }
    return null;
  })();

  const visibleComments = comments.filter((c) => !get(c, 'meta.valuesChanged'));

  const customFields = get(taskType, 'custom_fields', []);
  const customFieldsById = {};
  customFields.forEach((cf) => { customFieldsById[cf.id] = cf; });

  const stdReqs = taskType ? getStandardFieldRequirements(taskType) : [];
  const isFieldEnabled = (fid) => {
    const f = stdReqs.find((s) => s.id === fid);
    return f ? f.is_enabled !== false : true;
  };

  const renderReference = () => {
    if (!hasReference) return null;
    return (
      <div key="f_reference" className={classes.referenceSection}>
        <span className={classes.fieldLabel}>Reference:</span>
        <span className={classes.fieldValue}>
          {refChecklist && <span>{refChecklist}</span>}
          {refCheckpoint && <span>{refChecklist ? ' > ' : ''}{refCheckpoint}</span>}
          {reportSection && <span>{get(reportSection, 'title', get(reportSection, 'name', 'Report Section'))}</span>}
          {scoreRefTag && <TaskScoreRef checkpoint={scoreRefTag} />}
        </span>
      </div>
    );
  };

  const renderDefaultField = (fid) => {
    if (fid === 'f_title') return null; // rendered as header
    if (fid === 'f_reference') return renderReference();
    if (!isFieldEnabled(fid)) return null;
    switch (fid) {
      case 'f_description':
        return get(task, 'description') ? (
          <FieldRow key={fid} classes={classes} label="Description">
            {formatFieldValue(get(task, 'description'), classes)}
          </FieldRow>
        ) : null;
      case 'f_assignee':
        return size(assignees) > 0 ? (
          <FieldRow key={fid} classes={classes} label="Assigned to">
            <span className={classes.fieldValue}>{assignees.map((a) => a.name).join(', ')}</span>
          </FieldRow>
        ) : null;
      case 'f_due_date':
        return dueDate ? (
          <FieldRow key={fid} classes={classes} label="Due date">
            <span className={classes.fieldValue}>{moment(dueDate).format('L')}</span>
          </FieldRow>
        ) : null;
      case 'f_trade':
        return size(tradeTypes) > 0 ? (
          <FieldRow key={fid} classes={classes} label="Trade">
            <span className={classes.fieldValue}>{tradeTypes.map((t) => getTradeName(t)).join(', ')}</span>
          </FieldRow>
        ) : null;
      case 'f_media':
        return <MediaBlock key={fid} classes={classes} label="Media" images={images} files={files} />;
      default:
        return null;
    }
  };

  const renderCustomField = (cf) => (
    <CustomFieldBlock key={cf.id} classes={classes} cf={cf} customFieldValues={customFieldValues} task={task} />
  );

  // Order fields using task type's field_order, falling back to default sequence
  const fieldOrder = get(taskType, 'field_order');
  const orderedFields = [];
  const referenceInOrder = fieldOrder && Array.isArray(fieldOrder) && fieldOrder.includes('f_reference');
  if (fieldOrder && Array.isArray(fieldOrder)) {
    fieldOrder.forEach((fid) => {
      if (fid === 'f_documents') return; // merged into f_media
      if (fid.startsWith('f_')) {
        const rendered = renderDefaultField(fid);
        if (rendered) orderedFields.push(rendered);
      } else if (customFieldsById[fid]) {
        orderedFields.push(renderCustomField(customFieldsById[fid]));
      }
    });
    // Append custom fields not in field_order (newly added)
    customFields.forEach((cf) => {
      if (!fieldOrder.includes(cf.id)) orderedFields.push(renderCustomField(cf));
    });
  } else {
    ['f_description', 'f_assignee', 'f_due_date', 'f_trade', 'f_media'].forEach((fid) => {
      const rendered = renderDefaultField(fid);
      if (rendered) orderedFields.push(rendered);
    });
    customFields.forEach((cf) => orderedFields.push(renderCustomField(cf)));
  }

  return (
    <div className={classes.taskCard}>
      <div className={classes.taskHeader}>
        <Typography className={classes.taskName}>
          {taskType ? `${taskType.name}: ` : ''}{get(task, 'name', 'Untitled')}
        </Typography>
        <Chip label={uppercaseFirst(status.title)} size="small" className={classes.statusChip} />
      </div>

      <FieldRow classes={classes} label="Created by">
        <span className={classes.fieldValue}>{author} - {moment(get(task, 'createdAt')).format('L')}</span>
      </FieldRow>

      {orderedFields}

      {!referenceInOrder && renderReference()}

      {size(visibleComments) > 0 && (
        <div className={classes.commentSection}>
          <Typography variant="caption" style={{ fontWeight: 'bold' }}>Comments ({size(visibleComments)})</Typography>
          {visibleComments.map((c, i) => (
            <div key={i} className={classes.comment}>
              <div className={classes.commentAuthor}>
                {get(c, 'author.name', 'Unknown')} - {moment(get(c, 'createdAt')).format('L h:mma')}
              </div>
              <div>{get(c, 'body')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TasksPrintSection = (props) => {
  const { assessment, printView } = props;
  const classes = useStyles();
  const assessment_id = get(assessment, 'data.id', get(assessment, 'id'));

  const { data: tasks, loading } = useRetrieveData(
    { url: '/tasks' },
    {
      waitFor: assessment_id ? undefined : null,
      build: ['tasks'],
      transformData: (d) =>
        (d || []).map((v) => {
          if (!v || !v.meta) return v;
          // json-api-normalizer camelCases all keys including nested meta keys.
          // Convert back to snake_case so property paths match task-type-utils expectations.
          const newmeta = {};
          for (let k in v.meta) {
            const val = v.meta[k];
            if (val && typeof val === 'object' && !Array.isArray(val)) {
              const nested = {};
              for (let nk in val) {
                nested[snakeCase(nk)] = val[nk];
              }
              set(newmeta, snakeCase(k), nested);
            } else {
              set(newmeta, snakeCase(k), val);
            }
          }
          v.meta = newmeta;
          return v;
        }),
      params: {
        filter: { assessment: assessment_id },
        include: 'images,files,assignees,authors,comments,comments.author,tradeTypes,scorecardScore,scorecardScore.checklist,scorecardScore.checkpoint,subScore,subScore.scorecardScore,reportSection',
      },
    }
  );

  const { data: taskTypesData, loading: taskTypesLoading } = useTaskTypes();

  useLoaded(props, [loading, taskTypesLoading]);

  if (!printView) return null;
  if (!size(tasks)) return <div className={classes.noTasks}>No tasks for this assessment</div>;

  return (
    <div className={classes.container}>
      <Typography className={classes.sectionTitle}>Tasks</Typography>
      {tasks.map((task) => (
        <TaskPrintCard key={task.id} task={task} taskTypesData={taskTypesData} />
      ))}
    </div>
  );
};

export default TasksPrintSection;
